import {
  useCallback,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

import { RippleCircle, type Ripple } from "@/components/touch-ripple-circle";

// Material Design's touch ripple: press feedback that starts under the pointer
// and washes outward across the whole surface.
//
// This is a port, not an interpretation. The legacy Spec Tool's buttons are MUI
// `contained` buttons, so their ripple is `@mui/material/ButtonBase`'s
// `TouchRipple` / `Ripple` pair. The geometry here, the exit timing in
// `touch-ripple-circle.tsx`, and the two keyframes and easing in `index.css` are
// all read from that source rather than eyeballed.
// (That theme leaves ripples on; the old stack only switches them off in its own
// test harness, `src/test-utils/render-in-theme.tsx` — which is a fair warning
// that the animation itself is not what to assert on.)
//
// Two deliberate departures from the original:
//
//   1. **Pointer events, not the mouse/touch pair.** MUI predates them, so it
//      wires seven handlers and keeps an `ignoringMouseDown` flag to discard the
//      synthetic mouse events phones fire after a touch. One `pointerdown` covers
//      every input device, and `pointercancel` — which the browser fires the
//      moment it claims a gesture for scrolling — replaces MUI's 80ms
//      `DELAY_RIPPLE` guess about whether a touch was a tap or a scroll.
//   2. **No focus pulsate by default.** MUI's buttons also breathe a centred
//      ripple while keyboard-focused. This app's buttons already draw a
//      focus-visible ring, so that would be a second, permanently-animating
//      focus affordance on top of one that already reads clearly. A surface
//      that draws **no ring of its own** has no such second affordance, and for
//      it the pulsate is the donor's only focus indicator — MUI's `Tab` is one
//      (#690). It opts in through `useTouchRipple({ focusPulsate })`.
//
// Keyboard activation *is* ported (owner ruling on #456, decision D5), but it
// is **opt-in per surface**, through `composeRippleProps`' `focusRipple`.
//
// D5's write-up justified the port with "MUI inks a keyboard press exactly as
// it inks a click". That is not true, and the arms below are why it looked
// true: `ButtonBase` gates both of them on `focusRipple`, which defaults to
// `false` (`ButtonBase.js:105`), and six of MUI's `ButtonBase` users turn it on
// while `Chip`, `CardActionArea`, `MenuItem` and `ListItemButton` do not. Every
// surface D5 actually reached has a donor in the "yes" column, so the ruling
// was right where it was applied and simply does not generalise — see
// `RippleOptions.focusRipple` for the roll-call, which is the one place it is
// written down.
//
// The arms are `ButtonBase.js:152-167`, and the details are all load-bearing:
//
//   - **Space only, never Enter.** `handleBeforeKeyDown` tests `event.key === ' '`.
//     Enter on a native button fires its click on *keydown* and MUI adds no
//     ripple for it, so inking Enter here would be a divergence, not a fix.
//   - **Only while focus-visible**, matching MUI's `focusVisible` guard — a
//     control that was clicked, not tabbed to, does not ink on a stray Space.
//     Latched when focus *arrives* (`ButtonBase.js` `handleFocus` →
//     `isFocusVisible(event.target)`), not re-read on the keydown: Chromium
//     flips a clicked button to `:focus-visible` the moment any key is pressed,
//     so asking at keydown time answers yes for every press and the guard is no
//     guard at all. Caught by a test that expected silence and got ink.
//   - **Not on auto-repeat** (`!event.repeat`), or a held Space would stack a
//     ripple per repeat.
//   - **Centred in position but not in size**, whatever `center` this hook was
//     given. A key event carries no coordinates, so MUI starts the ink at the
//     middle of the surface (`TouchRipple.js:85`) — but it still sizes the disc
//     with the corner-reach formula, because the centred-size branch keys off
//     the `centerRipple` *prop* alone (`:97`). The two decisions are separate
//     in the donor and have to be separate here: collapsing them drew a
//     203.5px disc on a Space press where the donor draws 252.9px.
//   - **Released on keyup**, where MUI does `stop(event, () => pulsate(event))`
//     — the pulsate arm is the focus breathing, so on a surface that has not
//     opted into it what remains of that line is the stop.
//   - **Released on blur too** (`ButtonBase.js:221`), which is also what keeps
//     ink off a control that loses focus mid-press — a Space on a menu trigger
//     opens the menu and takes focus with it, so the keyup never arrives.
/**
 * The eleven arms `useTouchRipple` drives, as one surface's handler props.
 *
 * **Eleven is MUI's list.** `ButtonBase` starts the ink on press and retires it
 * on each of the rest; the three at the bottom were missing from this port
 * until they were counted against the donor. Each is a real press that ends
 * without a `pointerup` or a `pointerleave`, so each left ink sitting on the
 * control for good:
 *
 *   - `contextmenu` (`ButtonBase.js:207`, wired at `:272`) — a right-press, or
 *     a long-press on touch, which opens the menu and never releases.
 *   - `dragleave` (`:208`, wired at `:279`) — a drag that began elsewhere
 *     leaving this control.
 *   - `touchmove` (`:220`, wired at `:281`) — a finger dragged *within* a held
 *     control. No pointer capture is taken, so the pointer arms see neither a
 *     leave nor a cancel while the finger stays inside the bounds.
 *
 * MUI gates all three on `disableTouchRipple`, as it does the press and release
 * arms, which is this port's `disableRipple`.
 */
export type RippleArms<E extends HTMLElement = HTMLElement> = {
  onPointerDown: React.PointerEventHandler<E>;
  onPointerUp: React.PointerEventHandler<E>;
  onPointerLeave: React.PointerEventHandler<E>;
  onPointerCancel: React.PointerEventHandler<E>;
  onKeyDown: React.KeyboardEventHandler<E>;
  onKeyUp: React.KeyboardEventHandler<E>;
  onFocus: React.FocusEventHandler<E>;
  onBlur: React.FocusEventHandler<E>;
  onContextMenu: React.MouseEventHandler<E>;
  onDragLeave: React.DragEventHandler<E>;
  onTouchMove: React.TouchEventHandler<E>;
};

/**
 * Merge a surface's own handlers with the ripple's, the ripple's running first.
 *
 * A wrapper that spreads `rippleTargetProps` onto an element **its caller can
 * also pass handlers to** needs this, and needs it on **all eleven** arms rather
 * than the one or two the wrapper happens to think about: the ripple starts on
 * `pointerdown` and is retired by whichever of `pointerup`, `pointerleave`,
 * `pointercancel`, `keyup` or `blur` arrives, so an arm a plain spread
 * clobbered leaves ink sitting on the control for good. In the other
 * direction, Radix sends its own `onPointerDown` through an `asChild` trigger,
 * and dropping that costs a menu its open-on-press.
 *
 * Written out arm by arm rather than mapped over the keys: the mapped version
 * loses the per-arm event type, and the event type is the only thing here a
 * compiler can check.
 *
 * `disableRipple` hands the caller's handlers straight back, which is exactly
 * what the donor's opt-out does: it drops the ripple and changes nothing else.
 *
 * **A surface with no caller handlers to merge does not need this, and several
 * skip it** — `checkbox-field.tsx`, `logout-page.tsx`, `app-dialog.tsx`,
 * `multi-select-field.tsx` and `catalog-batch-toolbar.tsx` spread
 * `rippleTargetProps` raw onto elements they own end to end. Note what that
 * costs them: the raw spread takes the Space arms **ungated**, so those
 * surfaces ink on Space whatever their donor does. It is the right answer for
 * every one of them today (their donors are `SwitchBase`, `Button` and
 * `IconButton`, all of which set `focusRipple`), but it is right by
 * coincidence rather than by the gate, so a raw spread is not a pattern to
 * copy onto a `MenuItem`-shaped surface.
 */
export interface RippleOptions {
  /**
   * MUI's `disableRipple`. The surface keeps every other behaviour and simply
   * stops inking.
   */
  disableRipple?: boolean;
  /**
   * MUI's `focusRipple` (`ButtonBase.js:105`), gating **the two Space arms and
   * nothing else** — `handleBeforeKeyDown` (`:152-159`) and
   * `handleBeforeKeyUp` (`:160-167`) both open with `if (focusRipple && …)`.
   *
   * **It defaults to `false`, as the donor's does, and that default is the
   * whole point.** The roll-call, which is the reason this option exists and
   * the one place it is written down — `ButtonBase` users that set it:
   *
   * | donor | sets it | at |
   * | --- | --- | --- |
   * | `Button` | yes | `Button.js:579` |
   * | `IconButton` | yes | `IconButton.js:217` |
   * | `SwitchBase` (Checkbox / Radio / Switch) | yes | `internal/SwitchBase.js:199` |
   * | `Tab` | yes | `Tab/Tab.js:264` |
   * | `ToggleButton` | yes | `ToggleButton/ToggleButton.js:183` |
   * | `Fab` | yes | `Fab/Fab.js:209` |
   * | `Chip` | **no** | zero occurrences in `Chip.js` |
   * | `CardActionArea` | **no** | zero occurrences |
   * | `MenuItem` | **no** | zero occurrences |
   * | `ListItemButton` | **no** | zero occurrences |
   *
   * All six that set it spell it `!disableFocusRipple`, so all six are on by
   * default. So in the source a chip, a card area and a menu row stay silent
   * under Space and ink only under a pointer, while a button, an icon button
   * and a checkbox all ink under both.
   *
   * `Autocomplete`'s popup indicator is not a separate answer: it is
   * `styled(IconButton)` (`Autocomplete.js:262`), so it inherits `yes`.
   *
   * Port a `Tab`, a `ToggleButton` or a `Fab` onto `RippleButton` and it needs
   * `focusRipple` passed — taking the default would be silently wrong. An
   * earlier draft of this comment claimed only `Button` and `IconButton` set
   * it; that was false, and this table is why it is now written out once
   * rather than paraphrased at each call site.
   *
   * `onFocus`/`onBlur` stay wired either way, because MUI wires them either
   * way: `handleBlur` stops the ripple through `useRippleHandler(ripple,
   * 'stop', …, false)` (`:221` — that trailing `false` is literal), and
   * `handleFocus` (`:229`) keeps the focus-visible bookkeeping. With the Space
   * arms off they are simply what ends a *pointer* press that loses focus.
   */
  focusRipple?: boolean;
}

export function composeRippleProps<E extends HTMLElement = HTMLElement>(
  ripple: ReturnType<typeof useTouchRipple>["rippleTargetProps"],
  theirs: Partial<RippleArms<E>> = {},
  { disableRipple = false, focusRipple = false }: RippleOptions = {},
): RippleArms<E> {
  const enabled = !disableRipple;
  // `useTouchRipple` always hands back all eleven arms, exactly as MUI's
  // `TouchRipple` always exposes `start`/`stop`; deciding which of them a Space
  // press is allowed to reach is `ButtonBase`'s job, and this composer is this
  // app's `ButtonBase`.
  const keyboard = enabled && focusRipple;

  return {
    onPointerDown: (event) => {
      if (enabled) ripple.onPointerDown(event);
      theirs.onPointerDown?.(event);
    },
    onPointerUp: (event) => {
      if (enabled) ripple.onPointerUp();
      theirs.onPointerUp?.(event);
    },
    onPointerLeave: (event) => {
      if (enabled) ripple.onPointerLeave();
      theirs.onPointerLeave?.(event);
    },
    onPointerCancel: (event) => {
      if (enabled) ripple.onPointerCancel();
      theirs.onPointerCancel?.(event);
    },
    onKeyDown: (event) => {
      if (keyboard) ripple.onKeyDown(event);
      theirs.onKeyDown?.(event);
    },
    onKeyUp: (event) => {
      if (keyboard) ripple.onKeyUp(event);
      theirs.onKeyUp?.(event);
    },
    onFocus: (event) => {
      if (enabled) ripple.onFocus(event);
      theirs.onFocus?.(event);
    },
    onBlur: (event) => {
      if (enabled) ripple.onBlur();
      theirs.onBlur?.(event);
    },
    onContextMenu: (event) => {
      if (enabled) ripple.onContextMenu();
      theirs.onContextMenu?.(event);
    },
    onDragLeave: (event) => {
      if (enabled) ripple.onDragLeave();
      theirs.onDragLeave?.(event);
    },
    onTouchMove: (event) => {
      if (enabled) ripple.onTouchMove();
      theirs.onTouchMove?.(event);
    },
  };
}

/**
 * Press feedback for one surface. Spread `rippleTargetProps` onto the element
 * being pressed and render `rippleLayer` inside it; the element needs to
 * establish a positioning context (`relative`) for the layer to fill it.
 */
export function useTouchRipple({
  center = false,
  focusPulsate = false,
  disabled = false,
}: {
  center?: boolean;
  /**
   * The surface is disabled: no layer, and no ink. MUI unmounts the
   * `TouchRipple` of a disabled `ButtonBase` (`ButtonBase.js:200,286`), so a
   * wash still fading when a press disables its own control — a month arrow
   * that reaches `minDate`, a button that goes busy — vanishes on the spot,
   * and a surface enabled again starts with an empty layer.
   */
  disabled?: boolean;
  /**
   * MUI's focus ripple (`ButtonBase.js:201-205`): while the surface holds
   * keyboard focus, a centred disc sits on it and breathes. Off by default —
   * see departure 2 above — and on only for a surface with no focus ring.
   */
  focusPulsate?: boolean;
} = {}) {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const nextKey = useRef(0);
  // **The layer mounts on first use, and then stays** — MUI's `LazyRipple`
  // (`useLazyRipple.js`, `ButtonBase.js:146,200,286`), which renders no
  // `TouchRipple` until the first `start`, `stop` or `pulsate` asks for one.
  // Visible, not just thrifty: an absolute span laid over a composited
  // neighbour — the calendar's view switch, over its `will-change` caret — is
  // composited with it, and Chromium then squashes the controls painted after
  // it (the month arrows) into that layer, off the pixel grid, where the
  // donor's rasterise crisp (#706).
  const [layerMounted, setLayerMounted] = useState(false);
  // Dropped as the surface disables, as an unmount drops the donor's state.
  const [wasDisabled, setWasDisabled] = useState(disabled);
  if (wasDisabled !== disabled) {
    setWasDisabled(disabled);
    if (disabled) setRipples([]);
  }

  // Where a press lands: the surface, and the pointer's point on it — `null`
  // for a press with no coordinates (a key, a focus), which MUI starts at the
  // middle (`TouchRipple.js:85`).
  const start = useCallback(
    (
      surface: HTMLElement,
      point: { clientX: number; clientY: number } | null,
      { pulsate = false }: { pulsate?: boolean } = {},
    ) => {
      // A disabled donor root is `pointer-events: none` (`ButtonBase.js:77`),
      // so a press on it never reaches a ripple. Chromium still sends
      // `pointerdown` to a disabled `<button>`, so the press is refused here
      // instead, or it would sit in state and play once the control enabled.
      if (disabled) return;
      // **The ripple layer's box, not the button's.** MUI measures everything
      // here off `.MuiTouchRipple-root` (`TouchRipple.js:74-103`), which is
      // `inset: 0` and so covers the host's *padding* box — inside its border.
      // The layer here is the same `absolute inset-0` span, so its coordinate
      // system is the padding box too, and measuring the border box instead
      // puts every number out by the border width: on the outlined menu
      // trigger, a 1px border made the disc 2px too wide and stood it 1px off
      // centre. `clientWidth`/`clientHeight` and `clientLeft`/`clientTop` are
      // that box.
      //
      // They are also **integers, where MUI's are fractional**, and that is a
      // deviation rather than a fidelity: MUI reads `rect.width` off
      // `getBoundingClientRect`, so its odd-bump below tests `39.99998 % 2`
      // and almost never fires. Ours tests `40 % 2` and does. The disc can
      // therefore come out up to 1.5px wider than the donor's — under the
      // threshold where anything is visible, and the alternative is
      // reintroducing the border-box error this whole block exists to fix.
      const rect = surface.getBoundingClientRect();
      const width = surface.clientWidth;
      const height = surface.clientHeight;
      // **Two decisions, not one.** MUI centres the ink's *position* when the
      // event carries no coordinates — a keyboard press — or when
      // `centerRipple` is set (`TouchRipple.js:85`), but it only uses the
      // centred *size* formula when the prop is set (`:97`). A Space press on
      // a plain `Button` therefore blooms from the middle at full corner-reach
      // size. Driving both off one flag made that disc 203.5px where the donor
      // draws 252.9px — a fifth too small, and invisible to the eye.
      // A pulsate is `center` in both senses: `TouchRipple.js:338` defaults
      // `center` to `centerProp || options.pulsate`.
      const centeredSize = center || pulsate;
      // **Centred, for the controls MUI centres.** `SwitchBase` passes
      // `centerRipple` (`internal/SwitchBase.js:198`), so a checkbox's wash starts
      // at the middle of its disc however it was pressed, and is sized to fill
      // that disc rather than to reach a far corner: `sqrt((2w² + h²)/3)`, bumped
      // to an odd number because mobile Chrome skips the animation on even ones
      // (`TouchRipple.js:96-103`). Off the pointer instead, a 38px checkbox
      // washed ~56px from wherever it was hit and hit its own clip immediately.
      const x =
        point && !centeredSize
          ? Math.round(point.clientX - rect.left - surface.clientLeft)
          : Math.round(width / 2);
      const y =
        point && !centeredSize
          ? Math.round(point.clientY - rect.top - surface.clientTop)
          : Math.round(height / 2);

      let size: number;
      if (centeredSize) {
        size = Math.sqrt((2 * width ** 2 + height ** 2) / 3);
        if (size % 2 === 0) size += 1;
      } else {
        // MUI's other sizing: the circle has to reach the corner furthest from the
        // press, so each axis is measured to its far edge, doubled, and the two
        // combined as a diagonal. The +2 is the original's, and covers the
        // rounding above.
        const sizeX = Math.max(Math.abs(width - x), x) * 2 + 2;
        const sizeY = Math.max(Math.abs(height - y), y) * 2 + 2;
        size = Math.sqrt(sizeX ** 2 + sizeY ** 2);
      }

      const key = nextKey.current;
      nextKey.current += 1;
      setLayerMounted(true);
      setRipples((current) => [...current, { key, x, y, size, leaving: false, pulsate }]);
    },
    [center, disabled],
  );

  // `TouchRipple.js:390-394` — a centred start with nothing to press.
  const pulsate = useCallback(
    (surface: HTMLElement) => start(surface, null, { pulsate: true }),
    [start],
  );

  // Whether the focus this surface currently holds is the kind that draws a
  // focus ring — a ref rather than state, because nothing renders from it.
  const focusVisible = useRef(false);

  // Retires the oldest ripple still held down. Ripples stack, so a release
  // answers one press rather than clearing the surface.
  const stop = useCallback(() => {
    if (disabled) return;
    setLayerMounted(true);
    setRipples((current) => {
      const index = current.findIndex((ripple) => !ripple.leaving);
      if (index === -1) {
        return current;
      }
      const next = current.slice();
      next[index] = { ...next[index], leaving: true };
      return next;
    });
  }, [disabled]);

  // `ButtonBase.js:152-159` — a Space keydown on a keyboard-focused control
  // does `stop(event, () => start(event))`: the stop retires whatever ink is
  // already on the surface, so a second press replaces the first rather than
  // stacking on it.
  const keyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== " " || event.repeat) return;
      if (!focusVisible.current) return;
      stop();
      start(event.currentTarget, null);
    },
    [start, stop],
  );

  const focus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      focusVisible.current = event.target.matches(":focus-visible");
      if (focusPulsate && focusVisible.current) pulsate(event.currentTarget);
    },
    [focusPulsate, pulsate],
  );

  const blur = useCallback(() => {
    focusVisible.current = false;
    stop();
  }, [stop]);

  // `ButtonBase.js:160-167` — `stop(event, () => pulsate(event))`: the press's
  // ink retires, and a surface that breathes while focused starts again.
  const keyUp = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== " ") return;
      stop();
      if (focusPulsate && focusVisible.current) pulsate(event.currentTarget);
    },
    [stop, focusPulsate, pulsate],
  );

  const handleExited = useCallback((key: number) => {
    setRipples((current) => current.filter((ripple) => ripple.key !== key));
  }, []);

  return {
    rippleTargetProps: {
      onPointerDown: (event: PointerEvent<HTMLElement>) => start(event.currentTarget, event),
      onPointerUp: stop,
      // No pointer capture is taken, so a press dragged off the surface ends
      // with a leave and never a release.
      onPointerLeave: stop,
      onPointerCancel: stop,
      onKeyDown: keyDown,
      onKeyUp: keyUp,
      onFocus: focus,
      // A control that loses focus mid-press never sees its keyup — pressing
      // Space on a menu trigger opens the menu and takes focus with it — so
      // without this the ink would sit there for good.
      onBlur: blur,
      // The three MUI retires on that the pointer arms do not reach
      // (`ButtonBase.js:207,208,220`). Each is a press that ends without a
      // release or a leave; see `RippleArms`.
      onContextMenu: stop,
      onDragLeave: stop,
      onTouchMove: stop,
    },
    rippleLayer:
      layerMounted && !disabled ? (
        <span
          aria-hidden="true"
          data-slot="ripple-layer"
          // `rounded-[inherit]` so the clip follows the surface's own corners —
          // which is also why the surface itself needs no `overflow-hidden`, and
          // keeps its focus ring paintable outside its bounds.
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
        >
          {ripples.map((ripple) => (
            <RippleCircle key={ripple.key} ripple={ripple} onExited={handleExited} />
          ))}
        </span>
      ) : null,
  };
}
